import { isLATAMTenant } from '@common/login/selectors';
import {
    getFeatureToggles,
    getPerformReducedBus,
    getPerformVehicles,
} from '@common/permissions/selectors';
import Box from '@components/Box';
import Column from '@components/Column';
import ConditionalTooltip from '@components/ConditionalTooltip';
import OperatingPeriod from '@components/OperatingPeriod';
import { getSelectedVehicleFuelTypes } from '@data/selectors';
import OperationalDays from '@features/operationalDays';
import PDFDownloader from '@features/pdf/detail/VehiclePDFDownloader';
import { hideSidebar, showSidebar, sidebarData } from '@features/sidebar';
import { getActiveDateRange } from '@features/ui/selectors';
import EmptyState from '@rio-cloud/rio-uikit/EmptyState';
import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import {
    combustionColumnNames,
    commonColumnNames,
    electricColumnNames,
    getColumns,
    hydrateColumns,
} from '@utils/columns';
import { extractVehicleName } from '@utils/stringFormatters';
import { trackingAttributes } from '@utils/tracking';
import _ from 'lodash';
import moment from 'moment';
import { useCallback } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Column as ColumnType } from 'src/columns/createColumn';
import { State } from 'src/setup/types';
import {
    Attribute,
    DateRange,
    HydratedEntity,
    HydratedEntityWithChildren,
    Id,
    Vehicle,
} from 'src/types';

import { getSignal, VehicleFuelType } from '../../api';
import { VEHICLE_ROUTE } from '../../constants/routes';
import convertToRows from '../../utils/convertToRows';
import { openRow } from './actions';
import { getTopColumns } from './columns';
import { entitySidePanelSelection } from './selectors';
import Tabs from './SidepanelTabs';
import { decorateWithAdditionalInformation } from './utils';

const isValidDate = (date: Date | undefined) => date && moment(date).isValid();
const convertToRow = convertToRows(3);

export const FormattedOperationDays = ({ entity }: { entity: HydratedEntity }) => (
    <div className=" margin-bottom-5">
        <label className="margin-bottom-0 text-size-12">
            <FormattedMessage id="operationDays" />
        </label>
        <div className="text-size-18 ellipsis-1">
            <OperationalDays
                operatingDays={getSignal(entity, 'operatingDays', undefined)}
                totalNumberOfDays={getSignal(entity, 'totalNumberOfDays', undefined)}
            />
        </div>
    </div>
);

export type Props = {
    entity: Omit<HydratedEntityWithChildren, 'level'>;
    level: number | undefined;
    dateRange: DateRange;
    onClose: () => void;
    hasPerformVehicle: boolean;
    performVehicleIds: Id[];
    performReducedBusIds: Id[];
    onDriverClick: (data: { id: Id; level: number; dateRange: DateRange }) => void;
    openParent: (id: Id) => void;
    isLATAMTenant?: boolean;
    shouldShowOvertakeInCruise?: boolean;
    shouldUseV3Ratings?: boolean;
    selectedVehicleFuelTypes: Set<VehicleFuelType>;
    attributes?: Attribute[];
    hideStandSAndDrivingConsumption?: boolean;
};
export const Sidepanel = ({
    entity,
    level,
    dateRange,
    onClose,
    hasPerformVehicle,
    performVehicleIds,
    performReducedBusIds,
    onDriverClick,
    openParent,
    isLATAMTenant = false,
    shouldShowOvertakeInCruise = false,
    shouldUseV3Ratings = false,
    selectedVehicleFuelTypes,
    hideStandSAndDrivingConsumption = false,
    attributes = [],
}: Props) => {
    const onDriverNameClick = useCallback(
        (driver: { id: Id }) => {
            // TODO: figure out why id needs to be an array!?
            onDriverClick({ id: driver.id, level: 2, dateRange });
            openParent(entity.id);
        },
        [onDriverClick, openParent, entity, dateRange],
    );

    if (
        !isValidDate(getSignal<Date | undefined>(entity, 'start', undefined)) ||
        !isValidDate(getSignal<Date | undefined>(entity, 'end', undefined))
    ) {
        return (
            <Sidebar fly onClose={onClose} position={'right'} width={1000}>
                <div className="padding-20 padding-top-15">
                    <EmptyState
                        headline={<FormattedMessage id="noData" />}
                        message={<FormattedMessage id="noData.explanation" />}
                    />
                </div>
            </Sidebar>
        );
    }

    const vehicle = extractVehicleName(_.get(entity, 'vehicles[0]', {} as Vehicle));
    const isElectricVehicle = _.get(entity, 'vehicles[0]')?.fuelType === VehicleFuelType.ELECTRIC;
    const shouldShowFuelType = selectedVehicleFuelTypes.has(VehicleFuelType.ELECTRIC);
    const entityWithAdditionalInformation = decorateWithAdditionalInformation({
        entity,
        performVehicleIds,
        performReducedBus: performReducedBusIds,
        shouldShowFuelType,
    });

    const isDriverNameClickable =
        hasPerformVehicle && level === 1 && entity.children && entity.children.length > 1;
    const topColumns = hydrateColumns(
        getTopColumns(
            isDriverNameClickable,
            onDriverNameClick as (driver: unknown) => void,
            hasPerformVehicle,
        ).filter((c): c is ColumnType => !!c),
        attributes,
    );

    const topRows = convertToRow(
        topColumns.map((column) => (
            <Column key={column.key} column={column} entity={entityWithAdditionalInformation} />
        )),
    );

    const startDate = dateRange.start;
    const endDate =
        dateRange.end.getHours() === 0 && dateRange.end.getMinutes() === 0
            ? moment(dateRange.end).subtract(1, 'minutes').toDate()
            : dateRange.end;

    const vehicleSpecialColumns = getColumns(
        isElectricVehicle ? electricColumnNames : combustionColumnNames,
    );
    let vehicleColumns = hydrateColumns(
        vehicleSpecialColumns
            .concat(getColumns(commonColumnNames))
            .filter((c): c is ColumnType => !!c),
        attributes,
    );

    if (hideStandSAndDrivingConsumption && isElectricVehicle) {
        const hiddenElectricColumns = [
            'electricAverageDrivingConsumption',
            'electricAverageIdlingConsumption',
        ];

        vehicleColumns = vehicleColumns.filter(
            (column) => !hiddenElectricColumns.includes(column.key),
        );
    }

    const updateLabelId = (arr: ColumnType[], key: string, newLabelId: string) => {
        const obj = arr.find((item) => item.key === key);
        if (obj) obj.labelId = newLabelId;
        return arr;
    };

    const idlingTimeLabelId = isElectricVehicle ? 'idlingTimeBev' : 'idlingTime';

    vehicleColumns = updateLabelId(vehicleColumns, 'idlingTime', idlingTimeLabelId);

    let displayedVehicleColumns = vehicleColumns
        .map((column: ColumnType) => (
            <Column key={column.key} column={column} entity={entity as HydratedEntity} />
        ))
        .concat([<FormattedOperationDays key="operationDays" entity={entity} />]);
    const vehicleRows = convertToRow(displayedVehicleColumns);
    return (
        <Sidebar
            fly
            title={
                <div className="padding-left-10 justify-content-between flex align-items-center">
                    <FormattedMessage
                        id="vehicleAnalysis.sidebar.title.oneVehicle"
                        values={{
                            vehicle,
                            start: <FormattedDate value={startDate} />,
                            end: <FormattedDate value={endDate} />,
                        }}
                    />
                </div>
            }
            onClose={onClose}
            position={'right'}
            width={1000}
            bodyClassName={'overflow-y-scroll'}
            headerButtons={
                <ConditionalTooltip enabled={!hasPerformVehicle}>
                    <div>
                        <PDFDownloader
                            attributes={attributes}
                            entity={entity}
                            dateRange={dateRange}
                            level={level}
                            performVehicleIds={performVehicleIds}
                            shouldUseV3Ratings={shouldUseV3Ratings}
                        />
                    </div>
                </ConditionalTooltip>
            }
        >
            <OperatingPeriod
                start={getSignal<Date | undefined>(entity, 'start', undefined) as Date}
                end={getSignal<Date | undefined>(entity, 'end', undefined) as Date}
            />
            <div
                className="padding-20 padding-top-15"
                data-test="VehicleSidePanel"
                {...trackingAttributes(
                    'visibility',
                    'perform, detailsPanel',
                    'openVehicleDetailsPanel',
                    'openDetailsPanel, fleetAnalysis',
                )}
            >
                <Box rows={topRows} />
                <Box rows={vehicleRows} hasBorder={false} />
                {!isLATAMTenant && (
                    <Tabs
                        level={level}
                        dateRange={dateRange}
                        entity={entity}
                        hasPerformVehicle={hasPerformVehicle}
                        performReducedBusIds={performReducedBusIds}
                        shouldShowOvertakeInCruise={shouldShowOvertakeInCruise}
                        isElectricVehicle={isElectricVehicle}
                    />
                )}
            </div>
        </Sidebar>
    );
};

const mapStateToProps = (state: State) => {
    const sideBarData = sidebarData(state, VEHICLE_ROUTE);
    const entityWithChildren = entitySidePanelSelection(state);

    const performVehicles = getPerformVehicles(state);
    const vehicleIds = (entityWithChildren?.vehicles || []).map((vehicle) => vehicle.vehicleId);
    const hasPerformVehicle = _.intersection(vehicleIds, performVehicles).length !== 0;
    const performReducedBus = getPerformReducedBus(state);

    return {
        entity: entityWithChildren || { id: '', vehicles: [], drivers: [], children: [] },
        level: _.get(sideBarData, 'level'),
        dateRange: getActiveDateRange(state),
        hasPerformVehicle,
        performVehicleIds: performVehicles,
        performReducedBusIds: performReducedBus,
        isLATAMTenant: isLATAMTenant(state),
        shouldShowOvertakeInCruise: getFeatureToggles(state).showOvertakeInCruise as boolean,
        shouldUseV3Ratings: getFeatureToggles(state).newOvertakeModel as boolean,
        selectedVehicleFuelTypes: getSelectedVehicleFuelTypes(state),
        attributes: sideBarData?.attributes,
        hideStandSAndDrivingConsumption: getFeatureToggles(state)
            .hideStandSAndDrivingConsumption as boolean,
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    onClose: () => dispatch(hideSidebar()),
    onDriverClick: (data: { id: Id; level: number; dateRange: DateRange }) =>
        dispatch(showSidebar({ data, type: VEHICLE_ROUTE })),
    openParent: (id: Id) => dispatch(openRow({ id })),
});

export default connect(mapStateToProps, mapDispatchToProps)(Sidepanel);
